'use client';

import { Button } from '@/components/button';
import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import type { ColorPluginData } from '@/components/customFields/types';
import { RichText } from '@/components/richText/RichText';
import { ColorTheme } from '@/helper/enums';
import { trimSlashes } from '@/helper/menuUtils';
import type { TagSignaturePayload } from '@/helper/security/tagSignatures.types';
import tracking, { type SliderImpression } from '@/helper/tracking';
import { linkToString } from '@/helper/urls';
import ArrowForwardIcon from '@/public/icons/pfeil-rechts-line.svg';
import type {
  ComponentActionCalendarLayoutStoryblok,
  ComponentActionCalendarTileStoryblok,
  MultilinkStoryblok,
} from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './ActionCalendarTile.module.scss';

// Dynamically import the ModalBox component with SSR disabled
const ModalBox = dynamic(() => import('@/components/modalBox/ModalBox'), { ssr: false });

export type ActionCalendarTileVariants = ComponentActionCalendarLayoutStoryblok['tile_variant'];
export type ActionCalendarTileStatus = 'expired' | 'valid' | 'upcoming';
export type FullComponentActionCalendarTileStoryblok = ComponentActionCalendarTileStoryblok & {
  icon_tile_color?: ColorPluginData;
};

const ActionCalendarTile: React.FC<{
  index?: number;
  hideDayNumber?: boolean;
  expiredText: string;
  variant: ActionCalendarTileVariants;
  blok: FullComponentActionCalendarTileStoryblok;
  globalModalCtaLink?: Exclude<MultilinkStoryblok, { linktype?: 'email' } | { linktype?: 'asset' }>;
  tagSignaturePayload?: TagSignaturePayload;
}> = ({ blok, variant = 'icon-tile', hideDayNumber, expiredText, globalModalCtaLink, index, tagSignaturePayload }) => {
  const {
    number,
    activation_date,
    deactivation_date,
    icon,
    icon_tile_color,
    image,
    modal_title,
    modal_description,
    modal_photo_1,
    modal_photo_2,
    modal_cta_link,
  } = blok;
  const pathname = usePathname();
  const router = useRouter();
  const { t } = useTranslation();
  const dayNumber = number || new Date(activation_date).getDate();
  const [openModal, setOpenModal] = useState(false);
  const toggleModal = () => {
    setOpenModal((prevState) => !prevState);
  };

  const actionStatus = useMemo((): ActionCalendarTileStatus => {
    const now = dayjs();

    const activation = dayjs(activation_date).startOf('day');
    const deactivation = dayjs(deactivation_date).endOf('day');

    // Handle invalid cases
    // 1- If activation and deactivation date are exactly the same
    // 2- If the date range is invalid
    if (activation.isSame(deactivation, 'minutes') || deactivation.isBefore(activation)) return 'expired';

    // Handle valid cases
    if (now.isBefore(activation)) return 'upcoming';
    if (now.isAfter(deactivation)) return 'expired';

    return 'valid';
  }, [activation_date, deactivation_date]);

  const onActionTileClick = () => {
    if (actionStatus === 'upcoming') return;
    // Open modal box
    toggleModal();
  };

  // If this tile(door) does not have its own CTA link, the global link will be applied.
  // Own CTA link has priority.
  const ctaLink = useMemo(() => {
    // Check the prop (cached_url) because it always has a value, whether the link is internal or external.
    return modal_cta_link?.cached_url ? modal_cta_link : globalModalCtaLink;
  }, [modal_cta_link, globalModalCtaLink]);

  const onParticipatingButtonClick = () => {
    if (actionStatus === 'valid' && ctaLink?.cached_url) {
      // Tracking
      const pageName = trimSlashes(pathname).split('/').slice(-1)[0]?.toLowerCase() ?? 'aktionskalender';
      const impression: SliderImpression = {
        location: pageName,
        type: 'teaser',
        name: `${pageName}-${dayNumber}`,
        title: '',
        segment: '',
      };
      tracking.teaserSliderClick(impression);

      const url = new URL(linkToString(ctaLink), window.location.origin);

      // The params should be only added for internal links!
      if (ctaLink.linktype === 'story') {
        const day = String(dayNumber);
        url.searchParams.set('tag', day);
        if (tagSignaturePayload) {
          url.searchParams.set('tag_sig', tagSignaturePayload.sig);
          url.searchParams.set('tag_iat', String(tagSignaturePayload.iat));
          url.searchParams.set('tag_exp', String(tagSignaturePayload.exp));
        }
      }

      router.push(url.href);
    }
  };

  // This maps the values of the prop 'blok.icon_tile_color'
  // stored in Storyblok to CSS classes defined in the SCSS file.
  const iconTileColor: { [key in string]: Exclude<ColorTheme, ColorTheme.White> } = {
    green300: ColorTheme.Green,
    green200: ColorTheme.LightGreen,
    green600: ColorTheme.StandardGreen,
    green800: ColorTheme.DarkGreen,
    blue300: ColorTheme.Blue,
    orange300: ColorTheme.Orange,
    magenta300: ColorTheme.Magenta,
    red300: ColorTheme.Red,
  };

  const selectedColorTheme = icon_tile_color?.name ? iconTileColor[icon_tile_color.name] : undefined;

  const ariaLabel = useMemo(() => {
    // If the day number is not shown, use the index number (1, 2, 3, etc..).
    const doorNumber = hideDayNumber ? index : dayNumber;
    let doorState = '';
    switch (actionStatus) {
      case 'expired':
        doorState = expiredText;
        break;
      case 'upcoming':
        doorState = t('Noch nicht aktiv');
        break;
      case 'valid':
        doorState = t('Aktiv');
        break;
    }

    return `${t('Türchen')} ${doorNumber} ${doorState}`;
  }, [actionStatus, dayNumber, expiredText, hideDayNumber, index, t]);

  return (
    <>
      <button
        {...storyblokEditable(blok)}
        aria-label={ariaLabel}
        data-testid="action-calendar-tile"
        disabled={actionStatus === 'upcoming'}
        onClick={onActionTileClick}
        className={classNames(
          'actionCalenderTile',
          classes.actionCalenderTile,
          selectedColorTheme && classes[selectedColorTheme],
          classes[actionStatus!],
          { [classes.iconVariant!]: variant === 'icon-tile' },
          { [classes.photoLightVariant!]: variant === 'photo-tile-light' },
          { [classes.photoDarkVariant!]: variant === 'photo-tile-dark' }
        )}
      >
        {!hideDayNumber && (
          <span className={classes.dayNumber} data-testid="action-calendar-tile__day-number">
            {dayNumber}
          </span>
        )}

        {variant === 'icon-tile' && icon && (
          <StoryblokImage
            ariaHidden={true}
            data-testid="action-calendar-tile__icon"
            className={classNames('actionCalenderTileIcon', classes.icon)}
            image={icon}
          />
        )}

        {(variant === 'photo-tile-light' || variant === 'photo-tile-dark') && image && (
          <>
            <StoryblokImage
              data-testid="action-calendar-tile__image"
              ariaHidden={true}
              className={classes.image}
              image={image}
            />
            <div aria-hidden="true" className={classes.imageOverlay}></div>
          </>
        )}

        {actionStatus === 'expired' && (
          <>
            <div aria-hidden="true" className={classes.expiredBlurOverlay}></div>
            <div aria-hidden="true" className={classes.expiredBlendOverlay}></div>
            <span data-testid="action-calendar-tile__expired-text" className={classes.expiredText}>
              {expiredText}
            </span>
          </>
        )}

        {(actionStatus === 'expired' || variant === 'photo-tile-light' || variant === 'photo-tile-dark') && (
          <div aria-hidden="true" className={classes.overlayOutline}></div>
        )}
      </button>

      <ModalBox
        data-testid="action-calendar-tile__modal-box"
        heading={modal_title}
        open={openModal}
        large={false}
        toggleOpen={toggleModal}
        className={classes.tileModal}
        theme={selectedColorTheme ?? ColorTheme.Green}
      >
        <div className={classes.modalContent}>
          {modal_description && <RichText text={modal_description} className={classes.description} />}
          {actionStatus === 'valid' && ctaLink?.cached_url && (
            <Button onClick={onParticipatingButtonClick} className={classes.actionButton}>
              {t('Jetzt mitmachen!')} <ArrowForwardIcon aria-hidden />
            </Button>
          )}
          {modal_photo_1 && <StoryblokImage image={modal_photo_1} className={classes.photo} />}
          {modal_photo_2 && <StoryblokImage image={modal_photo_2} className={classes.photo} />}
        </div>
      </ModalBox>
    </>
  );
};

export default ActionCalendarTile;
